import { ChangeEvent, FormEvent, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SEGMENT_OPTIONS = ['Financial Services', 'Corporate', 'Professional Services', 'Law', 'Other'];
const OTHER = 'Other';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const REQUIRED_FIELDS = ['firstName', 'lastName', 'email', 'message'] as const;
type RequiredField = (typeof REQUIRED_FIELDS)[number];
type FieldName = RequiredField | 'company' | 'segment' | 'segmentOther';
type Values = Record<FieldName, string>;
type Errors = Partial<Record<RequiredField, string>>;

const EMPTY_VALUES: Values = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  segment: '',
  segmentOther: '',
  message: ''
};

const validate = (values: Values): Errors => {
  const errors: Errors = {};
  if (!values.firstName.trim()) errors.firstName = 'First name is required.';
  if (!values.lastName.trim()) errors.lastName = 'Last name is required.';
  if (!values.email.trim()) errors.email = 'Email is required.';
  else if (!EMAIL_RE.test(values.email.trim())) errors.email = 'Please enter a valid email address.';
  if (!values.message.trim()) errors.message = 'Please tell us how our team can help.';
  return errors;
};

export const SupportForm = (): JSX.Element => {
  const [values, setValues] = useState<Values>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const clearError = (field: RequiredField) => setErrors(({ [field]: _removed, ...rest }) => rest);

  const setField = (name: FieldName, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if ((errors as Record<string, string | undefined>)[name]) {
      clearError(name as RequiredField);
    }
  };

  // Shared props for every text field; required fields layer on `requiredProps`.
  const fieldProps = (name: FieldName, label: string, placeholder: string) => ({
    className: `field-${name}`,
    label,
    placeholder,
    fullWidth: true,
    value: values[name],
    onChange: (e: ChangeEvent<HTMLInputElement>) => setField(name, e.target.value)
  });

  const requiredProps = (name: RequiredField) => ({
    required: true,
    error: Boolean(errors[name]),
    helperText: errors[name] ?? ' '
  });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setBanner('');
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('api/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: values.email.trim(),
          company: values.company.trim(),
          segment: (values.segment === OTHER ? values.segmentOther : values.segment).trim(),
          message: values.message.trim()
        })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.ok) {
        setSubmitted(true);
        return;
      }
      if (data && data.errors) {
        setErrors(data.errors as Errors);
      }
      setBanner((data && data.error) || 'Something went wrong. Please try again.');
    } catch {
      setBanner('Unable to submit right now. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className='support-card' sx={{ width: '100%', maxWidth: 720 }}>
      <Typography
        variant='h4'
        component='h1'
        className='support-title'
        sx={{
          textAlign: 'center',
          fontWeight: 700,
          // Match the Figma heading (36px); scale down a touch on narrow viewports.
          fontSize: { xs: '28px', sm: '36px' },
          lineHeight: 1.25,
          mt: 1,
          mb: 2
        }}
      >
        Get myGLG MCP Support
      </Typography>

      {!submitted && (
        <Typography
          className='support-subtitle'
          sx={{ textAlign: 'center', maxWidth: 520, mx: 'auto', mb: 4, color: 'text.secondary' }}
        >
          Have a question about setting up, configuring, or using the myGLG MCP? Fill out the form below and a GLG
          representative will get back to you shortly.
        </Typography>
      )}

      {submitted ? (
        <Box className='support-confirmation' sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircleIcon className='support-confirmation__icon' sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant='h5' component='h2' sx={{ fontWeight: 700, mb: 1 }}>
            Thank you — your request has been received.
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            A GLG representative will be in contact with you shortly.
          </Typography>
        </Box>
      ) : (
        <Box component='form' className='support-form' onSubmit={handleSubmit} noValidate>
          <Stack spacing={2.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
              <TextField {...fieldProps('firstName', 'First Name', 'First Name')} {...requiredProps('firstName')} />
              <TextField {...fieldProps('lastName', 'Last Name', 'Last Name')} {...requiredProps('lastName')} />
            </Box>

            <TextField type='email' {...fieldProps('email', 'Email', 'Business Email')} {...requiredProps('email')} />

            <TextField {...fieldProps('company', 'Company', 'Company')} />

            <TextField select {...fieldProps('segment', 'Segment', 'Segment')}>
              {SEGMENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>

            {values.segment === OTHER && (
              <TextField {...fieldProps('segmentOther', 'Please specify', 'Enter your segment')} />
            )}

            <TextField
              multiline
              minRows={4}
              {...fieldProps('message', 'How Can Our Team Help You?', 'How can our team help you?')}
              {...requiredProps('message')}
            />

            {banner && (
              <Typography role='alert' sx={{ color: 'error.main', fontSize: 14 }}>
                {banner}
              </Typography>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button className='submit-button' type='submit' variant='contained' disabled={submitting} sx={{ px: 4 }}>
                {submitting ? 'Submitting…' : 'Submit Request'}
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
    </Box>
  );
};
